import React from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { useI18n } from "../../i18n";

// Two-checkbox GDPR consent block used by the contact form and the CRA
// assessment email gate.
export const ConsentCheckboxes = ({
  processing,
  marketing,
  onProcessingChange,
  onMarketingChange,
  showError,
  testIdPrefix = "consent",
}) => {
  const { t } = useI18n();
  const Brand = () => (
    <span className="whitespace-nowrap">{t.consent.brand}</span>
  );

  const Box = ({ checked, onChange, testId, ariaLabel, children }) => (
    <label
      className="flex items-start gap-3 cursor-pointer group select-none"
      data-testid={testId}
      onClick={(e) => {
        // Ignore clicks on nested links (privacy policy) so they still navigate.
        if (e.target.closest && e.target.closest("a")) return;
        e.preventDefault();
        onChange(!checked);
      }}
    >
      <span
        role="checkbox"
        aria-checked={checked}
        aria-label={ariaLabel}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            onChange(!checked);
          }
        }}
        className={`mt-0.5 shrink-0 w-5 h-5 rounded-sm border transition-colors flex items-center justify-center ${
          checked
            ? "bg-[#0052FF] border-[#0052FF]"
            : "bg-white/5 border-white/25 group-hover:border-white/50"
        }`}
      >
        {checked && <Check size={14} className="text-white" strokeWidth={3} />}
      </span>
      <span className="text-xs md:text-sm text-gray-400 leading-relaxed">
        {children}
      </span>
    </label>
  );

  return (
    <div className="space-y-4">
      <Box
        checked={processing}
        onChange={onProcessingChange}
        testId={`${testIdPrefix}-processing`}
        ariaLabel={t.consent.processingAria || "Consent: data processing"}
      >
        {t.consent.processing1}
        <Brand />
        {t.consent.processing2}
        <Link
          to="/datenschutz"
          target="_blank"
          className="text-[#3B82F6] underline underline-offset-2 hover:text-white transition-colors"
        >
          {t.consent.processingLink}
        </Link>
        {t.consent.processing3}
        <span className="text-red-400 ml-1" aria-hidden="true">*</span>
      </Box>

      {showError && !processing && (
        <p
          data-testid={`${testIdPrefix}-processing-error`}
          className="text-xs text-red-400 -mt-2 pl-8"
        >
          {t.consent.required}
        </p>
      )}

      <Box
        checked={marketing}
        onChange={onMarketingChange}
        testId={`${testIdPrefix}-marketing`}
        ariaLabel={t.consent.marketingAria || "Consent: marketing communication"}
      >
        {t.consent.marketing1}
        <Brand />
        {t.consent.marketing2}
      </Box>
    </div>
  );
};
